import React, { useState, useEffect } from "react";
import { ArrowLeft, Plus, Trash2, Users, AlertCircle } from "lucide-react";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle
} from "@/components/ui/card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useForm } from "react-hook-form";
import DashboardContainer from "../layout/DashboardContainer";
import { useParams, useNavigate } from "react-router-dom";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";

interface UserFeatureFlag {
    user_email: string;
    feature_flag_key: string;
    feature_flag_description: string;
    is_enabled: boolean;
    created_at: string;
    updated_at: string;
    created_by: string | null;
}

interface CreateUserOverrideForm {
    user_email: string;
    is_enabled: boolean;
}

const FeatureFlagUserManagement: React.FC = () => {
    const { flagKey } = useParams<{ flagKey: string }>();
    const [userOverrides, setUserOverrides] = useState<UserFeatureFlag[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState<boolean>(false);
    const [isCreating, setIsCreating] = useState<boolean>(false);
    const { toast } = useToast();
    const navigate = useNavigate();

    const form = useForm<CreateUserOverrideForm>({
        defaultValues: {
            user_email: "",
            is_enabled: true,
        },
    });

    const handleBackClick = () => {
        navigate("/operations/features");
    };

    useEffect(() => {
        if (flagKey) {
            fetchUserOverrides();
        }
    }, [flagKey]);

    const fetchUserOverrides = async () => {
        if (!flagKey) return;
        
        try {
            setIsLoading(true);
            setError(null);
            const response = await ApiService.request<UserFeatureFlag[]>(
                EndpointKeys.GET_USER_FEATURE_FLAGS,
                {
                    method: "GET",
                    queryParams: { flag_key: flagKey }
                }
            );
            setUserOverrides(response);
        } catch (err) {
            setError("Failed to load user overrides. Please try again later.");
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateUserOverride = async (data: CreateUserOverrideForm) => {
        if (!flagKey) return;
        
        setIsCreating(true);
        try {
            const response = await ApiService.request<UserFeatureFlag>(
                EndpointKeys.CREATE_USER_FEATURE_FLAG,
                {
                    method: "POST",
                    body: {
                        user_email: data.user_email,
                        flag_key: flagKey,
                        is_enabled: data.is_enabled
                    }
                }
            );

            if (response) {
                // Add the new override to the list
                setUserOverrides(prevOverrides => [...prevOverrides, response]);
                
                // Reset form and close dialog
                form.reset();
                setIsCreateDialogOpen(false);

                toast({
                    title: "Success",
                    description: "User override created successfully",
                });
            }
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to create user override. Please try again.",
                variant: "destructive",
            });
        } finally {
            setIsCreating(false);
        }
    };

    const handleRemoveUserOverride = async (userEmail: string) => {
        if (!flagKey) return;
        
        try {
            await ApiService.request(
                EndpointKeys.DELETE_USER_FEATURE_FLAG,
                {
                    method: "DELETE",
                    queryParams: { 
                        user_email: userEmail,
                        flag_key: flagKey
                    }
                }
            );

            // Remove the override from the list
            setUserOverrides(prevOverrides => 
                prevOverrides.filter(override => override.user_email !== userEmail)
            );

            toast({
                title: "Success",
                description: "User override removed successfully",
            });
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to remove user override. Please try again.",
                variant: "destructive",
            });
        }
    };

    const getStatusBadge = (isEnabled: boolean) => {
        return isEnabled ? (
            <Badge variant="default" className="flex items-center">
                Active
            </Badge>
        ) : (
            <Badge variant="outline" className="flex items-center">
                Inactive
            </Badge>
        );
    };

    if (isLoading) {
        return (
            <DashboardContainer
                title="User Management"
                headerContent={
                    <Button variant="ghost" onClick={handleBackClick}>
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Features
                    </Button>
                }
            >
                <div className="flex justify-center items-center py-8">
                    <p className="text-gray-500">Loading user overrides...</p>
                </div>
            </DashboardContainer>
        );
    }

    if (error) {
        return (
            <DashboardContainer
                title="User Management"
                headerContent={
                    <Button variant="ghost" onClick={handleBackClick}>
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Features
                    </Button>
                }
            >
                <div className="flex flex-col items-center justify-center py-8">
                    <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
                    <p className="text-red-600 text-center">{error}</p>
                    <Button onClick={fetchUserOverrides} className="mt-4">
                        Try Again
                    </Button>
                </div>
            </DashboardContainer>
        );
    }

    return (
        <DashboardContainer
            title={`User Overrides for "${flagKey}"`}
            headerContent={
                <Button variant="ghost" onClick={handleBackClick}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Features
                </Button>
            }
        >
            <Card>
                <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <CardTitle className="flex items-center">
                            <Users className="w-5 h-5 mr-2" />
                            User-Specific Overrides
                        </CardTitle>
                        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                            <DialogTrigger asChild>
                                <Button>
                                    <Plus className="w-4 h-4 mr-2" />
                                    Add User Override
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-[425px]">
                                <DialogHeader>
                                    <DialogTitle>Add User Override</DialogTitle>
                                    <DialogDescription>
                                        Create a user-specific override for the "{flagKey}" feature flag.
                                    </DialogDescription>
                                </DialogHeader>
                                <Form {...form}>
                                    <form onSubmit={form.handleSubmit(handleCreateUserOverride)} className="space-y-4">
                                        <FormField
                                            control={form.control}
                                            name="user_email"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>User Email</FormLabel>
                                                    <FormControl>
                                                        <Input 
                                                            placeholder="user@example.com" 
                                                            type="email"
                                                            {...field} 
                                                        />
                                                    </FormControl>
                                                    <FormDescription>
                                                        Email address of the user to create an override for
                                                    </FormDescription>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name="is_enabled"
                                            render={({ field }) => (
                                                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                                    <div className="space-y-0.5">
                                                        <FormLabel className="text-base">
                                                            Enable for this user
                                                        </FormLabel>
                                                        <FormDescription>
                                                            Whether this feature should be enabled for this specific user
                                                        </FormDescription>
                                                    </div>
                                                    <FormControl>
                                                        <Switch
                                                            checked={field.value}
                                                            onCheckedChange={field.onChange}
                                                        />
                                                    </FormControl>
                                                </FormItem>
                                            )}
                                        />
                                        <DialogFooter>
                                            <Button 
                                                type="button" 
                                                variant="outline" 
                                                onClick={() => setIsCreateDialogOpen(false)}
                                            >
                                                Cancel
                                            </Button>
                                            <Button type="submit" disabled={isCreating}>
                                                {isCreating ? "Creating..." : "Create Override"}
                                            </Button>
                                        </DialogFooter>
                                    </form>
                                </Form>
                            </DialogContent>
                        </Dialog>
                    </div>
                </CardHeader>
                <CardContent>
                    {userOverrides.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                            <p className="text-lg font-medium">No user overrides found</p>
                            <p className="text-sm">This feature flag uses the global setting for all users.</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>User Email</TableHead>
                                    <TableHead>Override Status</TableHead>
                                    <TableHead>Created At</TableHead>
                                    <TableHead>Created By</TableHead>
                                    <TableHead>Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {userOverrides.map((override) => (
                                    <TableRow key={override.user_email}>
                                        <TableCell className="font-medium">{override.user_email}</TableCell>
                                        <TableCell>{getStatusBadge(override.is_enabled)}</TableCell>
                                        <TableCell>{new Date(override.created_at).toLocaleDateString()}</TableCell>
                                        <TableCell>{override.created_by || "System"}</TableCell>
                                        <TableCell>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleRemoveUserOverride(override.user_email)}
                                                className="flex items-center text-red-600 hover:text-red-700"
                                            >
                                                <Trash2 className="w-4 h-4 mr-1"/>
                                                Remove
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </DashboardContainer>
    );
};

export default FeatureFlagUserManagement;
